import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { subscribe, search, listDistinct, exportJSON, clear } from '../../../services/bread/auditLogService';
import '../../../styles/accessibility.css';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [filters, setFilters] = useState({
    q: '',
    user: 'all',
    action: 'all',
    status: 'all',
    from: '',
    to: ''
  });
  const [distinctValues, setDistinctValues] = useState({ users: [], actions: [], statuses: [] });
  const [selectedLogs, setSelectedLogs] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 20;

  useEffect(() => {
    document.title = 'Audit Logs - Access Management';
    
    // Subscribe to audit log updates
    const unsubscribe = subscribe((data) => {
      setAllLogs(data);
      setDistinctValues(listDistinct());
      applyFilters(data);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    applyFilters(allLogs);
  }, [filters, allLogs]);

  const applyFilters = (data) => {
    const filtered = search({
      q: filters.q,
      user: filters.user,
      action: filters.action,
      status: filters.status,
      from: filters.from || null,
      to: filters.to || null
    });
    setLogs(filtered);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      q: '',
      user: 'all',
      action: 'all',
      status: 'all',
      from: '',
      to: ''
    });
  };

  const toggleSelectLog = (logId) => {
    setSelectedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const selectAllVisible = () => {
    const visibleIds = paginatedLogs.map(log => log.id);
    setSelectedLogs(new Set(visibleIds));
  };

  const deselectAll = () => {
    setSelectedLogs(new Set());
  };

  const handleExport = () => {
    const dataToExport = selectedLogs.size > 0
      ? logs.filter(log => selectedLogs.has(log.id))
      : logs;
    
    const dataStr = JSON.stringify(dataToExport, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `audit-logs-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    setShowExportModal(false);
    deselectAll();
  };

  const handleClearLogs = () => {
    if (window.confirm('Are you sure you want to clear all audit logs? This action cannot be undone.')) {
      clear();
    }
  };

  const getActionIcon = (action) => {
    if (action.includes('login')) return 'bi-box-arrow-in-right';
    if (action.includes('logout')) return 'bi-box-arrow-right';
    if (action.includes('create')) return 'bi-plus-circle';
    if (action.includes('update') || action.includes('edit')) return 'bi-pencil';
    if (action.includes('delete')) return 'bi-trash';
    if (action.includes('role')) return 'bi-shield-lock';
    if (action.includes('permission')) return 'bi-key';
    return 'bi-activity';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success': return 'bg-success';
      case 'failure': return 'bg-danger';
      case 'warning': return 'bg-warning';
      default: return 'bg-secondary';
    }
  };

  const formatTimestamp = (ts) => {
    const date = new Date(ts);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getRelativeTime = (ts) => {
    const now = new Date();
    const past = new Date(ts);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return formatTimestamp(ts);
  };

  // Pagination
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const paginatedLogs = logs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(logs.length / logsPerPage);

  const stats = {
    total: allLogs.length,
    success: allLogs.filter(l => l.status === 'success').length,
    failure: allLogs.filter(l => l.status === 'failure').length,
    today: allLogs.filter(l => {
      const logDate = new Date(l.ts).toDateString();
      const today = new Date().toDateString();
      return logDate === today;
    }).length
  };

  return (
    <div className="container-fluid py-4">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/bread/access-management">Access Management</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">Audit Logs</li>
        </ol>
      </nav>

      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="display-6 fw-bold mb-2">
                <i className="bi bi-clock-history me-2 text-primary"></i>
                Audit Logs
              </h1>
              <p className="text-muted mb-0">
                Track and monitor all security and access-related activities
              </p>
            </div>
            <div className="d-flex gap-2">
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={() => setShowExportModal(true)}
              >
                <i className="bi bi-download me-1"></i>
                Export
              </button>
              <button 
                className="btn btn-outline-danger btn-sm"
                onClick={handleClearLogs}
              >
                <i className="bi bi-trash me-1"></i>
                Clear All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Total Logs</h6>
                  <h3 className="mb-0">{stats.total}</h3>
                </div>
                <div className="bg-primary bg-opacity-10 p-3 rounded">
                  <i className="bi bi-file-earmark-text fs-4 text-primary"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-xl-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Today's Activity</h6>
                  <h3 className="mb-0">{stats.today}</h3>
                </div>
                <div className="bg-info bg-opacity-10 p-3 rounded">
                  <i className="bi bi-calendar-check fs-4 text-info"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-xl-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Successful</h6>
                  <h3 className="mb-0">{stats.success}</h3>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded">
                  <i className="bi bi-check-circle fs-4 text-success"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-xl-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Failed</h6>
                  <h3 className="mb-0">{stats.failure}</h3>
                </div>
                <div className="bg-danger bg-opacity-10 p-3 rounded">
                  <i className="bi bi-x-circle fs-4 text-danger"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-6 col-lg-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search logs..."
                    value={filters.q}
                    onChange={(e) => handleFilterChange('q', e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-6 col-lg-2">
                  <select
                    className="form-select"
                    value={filters.user}
                    onChange={(e) => handleFilterChange('user', e.target.value)}
                  >
                    <option value="all">All Users</option>
                    {distinctValues.users.map(user => (
                      <option key={user} value={user}>{user}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-6 col-lg-2">
                  <select
                    className="form-select"
                    value={filters.action}
                    onChange={(e) => handleFilterChange('action', e.target.value)}
                  >
                    <option value="all">All Actions</option>
                    {distinctValues.actions.map(action => (
                      <option key={action} value={action}>{action}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-6 col-lg-2">
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="all">All Status</option>
                    {distinctValues.statuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={clearFilters}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <span className="text-muted">
                    Showing {indexOfFirstLog + 1}-{Math.min(indexOfLastLog, logs.length)} of {logs.length} logs
                  </span>
                  {selectedLogs.size > 0 && (
                    <span className="badge bg-primary ms-2">
                      {selectedLogs.size} selected
                    </span>
                  )}
                </div>
                {paginatedLogs.length > 0 && (
                  <div className="btn-group btn-group-sm">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={selectAllVisible}
                    >
                      Select All
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={deselectAll}
                    >
                      Deselect All
                    </button>
                  </div>
                )}
              </div>

              {paginatedLogs.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-inbox fs-1 text-muted"></i>
                  <p className="text-muted mt-3">No audit logs found</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            onChange={(e) => {
                              if (e.target.checked) {
                                selectAllVisible();
                              } else {
                                deselectAll();
                              }
                            }}
                            checked={paginatedLogs.length > 0 && paginatedLogs.every(log => selectedLogs.has(log.id))}
                          />
                        </th>
                        <th>Timestamp</th>
                        <th>User</th>
                        <th>Action</th>
                        <th>Target</th>
                        <th>Status</th>
                        <th>IP Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedLogs.map(log => (
                        <tr key={log.id}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedLogs.has(log.id)}
                              onChange={() => toggleSelectLog(log.id)}
                            />
                          </td>
                          <td>
                            <div>
                              <small className="text-muted d-block">{formatTimestamp(log.ts)}</small>
                              <small className="text-primary">{getRelativeTime(log.ts)}</small>
                            </div>
                          </td>
                          <td>
                            <i className="bi bi-person-circle me-2 text-muted"></i>
                            {log.user}
                          </td>
                          <td>
                            <i className={`bi ${getActionIcon(log.action)} me-2 text-primary`}></i>
                            <code className="small">{log.action}</code>
                          </td>
                          <td>
                            <code className="small text-muted">{log.target}</code>
                          </td>
                          <td>
                            <span className={`badge ${getStatusBadge(log.status)}`}>
                              {log.status}
                            </span>
                          </td>
                          <td>
                            <code className="small">{log.ip}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <nav className="mt-3">
                  <ul className="pagination justify-content-center mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      ) {
                        return (
                          <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          </li>
                        );
                      } else if (page === currentPage - 3 || page === currentPage + 3) {
                        return <li key={page} className="page-item disabled"><span className="page-link">...</span></li>;
                      }
                      return null;
                    })}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Export Audit Logs</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowExportModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  {selectedLogs.size > 0
                    ? `Export ${selectedLogs.size} selected log(s)?`
                    : `Export all ${logs.length} filtered log(s)?`
                  }
                </p>
                <p className="text-muted small">
                  Logs will be exported as JSON format.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowExportModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleExport}
                >
                  <i className="bi bi-download me-1"></i>
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
